import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Logos } from '@yodlee/common-components/dist/assets';
import { ResponsiveEnums } from '@yodlee/common-components/dist/types/enumerations';
import Authentication from '@yodlee/common-components/dist/types/interfaces/authentication';
import { widgetLoader } from '@yodlee/common-components/dist/utilities';
import { Affix, Col, Icon, Layout, Modal, Row, Spin, Statistic } from 'antd';
import Title from 'antd/lib/typography/Title';
import { AxiosError, AxiosResponse } from 'axios';
import { set } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Route, RouteComponentProps, Switch } from 'react-router-dom';
import { LabelConfigs, UrlConfigs } from '../../configurations';
import { preferenceDefaults } from '../../configurations/dashboard';
import { ApplicationContext, MenuContext, WidgetContext } from '../../context';
import { BrandType } from '../../types/enumerations/common';
import { IPreferences } from '../../types/interfaces';
import { FinancialInstitution } from '../../types/interfaces/onload';
import { AmplitudeEventsUtils, DataUtils } from '../../utilities';
import { getAuthenticationDetails } from '../../utilities/authentication';
import getSocketConnection from '../../utilities/socket';
import FloatingChatButton from '../../components/floating-chat-button';
import MenuItems from './menu-items';
import { PackageLoader } from './package-loader';
import { v4 as uuidv4 } from 'uuid';
const SupportPage = widgetLoader(() => import("../support"));
const ServiceRequestPage = widgetLoader(() => import("../support/service-request"));
const StatusPage = widgetLoader(() => import("../status"));
const SplashPage = widgetLoader(() => import("../splash"));
const SettingsPage = widgetLoader(() => import("../settings"));
const ProductPage = widgetLoader(() => import("../product"));
const TrialsIframePage = widgetLoader(() => import("../trials/iframe"));
const ConsumerInsightsPage = widgetLoader(() => import("../trials/consumer-insights"));
const ProductAndMarketingInsightsPage = widgetLoader(() => import("../ieb/product-and-marketing-insights"));
const ManagementReportingPage = widgetLoader(() => import("../management-reporting/management-reporting-portal"));
const DeveloperChatPage = widgetLoader(() => import("../developer-chat"));
const MyAccountPage = widgetLoader(() => import("../my-account"));
const InternalToolsPage = widgetLoader(() => import("../internal-tools"));
const FeatureSettingPage = widgetLoader(() => import("../feature-setting"));
const DashboardPage = widgetLoader(() => import("../dashboard"));
const AnalyticsPage = widgetLoader(() => import("../analytics"));

const doLogout = (url: string) => {
    window.location.href = url;
}

interface IProps extends RouteComponentProps<any> {
}

const HomePage: React.FC<IProps> = ({ history, ...rest }) => {
    const [collapsed, setCollapsed] = useState<boolean>(true);
    const [onload, setOnload] = useState<any>();
    const [YccNewCaseManagementWidget, setYccNewCaseManagementWidget] = useState<any>();
    const [selectedBrand, setSelectedBrand] = useState<any>(undefined);
    const [isSplashOpen, updateIsSplashOpen] = useState<boolean>(false);
    const [logo, setLogo] = useState<any>();
    const [picture, setPicture] = useState<any>();
    const [banner, setBanner] = useState<any>();
    const [pref, setPref] = useState<IPreferences.Preferences | null>(null);
    const [financialInstitutions, setFinancialInstitutions] = useState<FinancialInstitution>();
    const isMD = useMediaQuery({ minWidth: ResponsiveEnums.SCREEN_SIZE.MD, minDeviceWidth: ResponsiveEnums.SCREEN_SIZE.MD });

    const [userPermission, setUserPermission] = useState<Array<any>>([]);
    const [userFeatures, setUserFeatures] = useState<Array<any> | null>(null);
    const [brandProducts, setBrandProducts] = useState<any | null>(null);

    const loggedInBrand = onload ? { id: onload.financialInstitution.id, name: onload.financialInstitution.name, type: onload.financialInstitution.type, brands: onload.financialInstitution.brands } : undefined;
    const isTier1 = loggedInBrand?.type?.id !== BrandType.YODLEE;

    const toggleCollapsed = (by: 'ARROW' | 'LOGO' | 'MENUITEM' | 'LAYOUT') => {
        collapsed ? AmplitudeEventsUtils.menuToggle(by, 'EXPAND') : AmplitudeEventsUtils.menuToggle(by, 'COLLAPSED')
        setCollapsed(!collapsed);
    }

    useEffect(() => {
        history.listen(() => {
            window.scrollTo(0, 0);
        })
    }, []);

    // hack for dashboard chart width issue on menu toggle
    useEffect(() => {
        if (window.location.hash === "#/dashboard" || window.location.hash === "#/") {
            setTimeout(() => {
                requestAnimationFrame(() => {
                    window.dispatchEvent(new Event('resize'));
                })
            }, 800);
        }
    }, [collapsed])

    const setIsSplashOpen = (flag: boolean) => {
        updateIsSplashOpen(flag);
        // mark splash complete if preference is available and flag === false and there splash complete is not true then update

        const authentication: Authentication = getAuthenticationDetails();

        DataUtils.preference(authentication).get()
            .then((response: AxiosResponse) => {
                let latestPrefData: any = {};
                try {
                    latestPrefData = { ...JSON.parse(response.data.preference || '{}') }
                } catch (e) { console.error(e) }
                if (latestPrefData && latestPrefData?.preferences?.isSplashCompleted !== true && flag === false) {
                    let updatedPref = set(latestPrefData, 'preferences.isSplashCompleted', true);
                    DataUtils.preference(authentication).put(updatedPref)
                        .then((response: AxiosResponse) => {
                            setPref(updatedPref);
                        }).catch((error: AxiosError<any,any>) => {
                            console.error(error);
                        });
                }
            }).catch((error: AxiosError<any,any>) => {
                console.error(error);
            });
    }

    useEffect(() => {
        let warningTimeout: NodeJS.Timeout | undefined;
        let logoutTimeout: NodeJS.Timeout | undefined;

        const authentication: Authentication = getAuthenticationDetails();
        if (!onload) {
            DataUtils.onload(authentication).get().then((onload) => {
                setOnload(onload)
                DataUtils.permission(authentication).get(onload.user.id).then(permission => setUserPermission(permission));
                DataUtils.userFeatures(authentication).get(onload.user.id).then((features) => {
                    setUserFeatures((features?.data || []))
                }).catch(() => setUserFeatures([]));
                DataUtils.brandProducts(authentication).get().then((products)=>{
                    setBrandProducts(products);
                }).catch(() => setBrandProducts({}));

                if (onload?.socketUrl) {
                    const socketUrl = onload?.socketUrl;
                    const socketAuthString = `brandId=${authentication.brandId}&rsession=${authentication.rsession}&brandName=${authentication.brandName}&appId=${authentication.applicationId}&yccUrl=${authentication.returnUrl}`;
                    const stompClient = getSocketConnection(socketUrl, socketAuthString);
                    stompClient.connect({}, (frame: any) => {
                        stompClient.subscribe(`/ws/${authentication.brandId}/cache/reload`, (data: any) => {
                            window.caches?.delete("onload");
                            DataUtils.onload(authentication).get().then((onloadResp) => {
                                setFinancialInstitutions(onloadResp.financialInstitution);
                            })
                        });
                    });
                }
            });
        } else {
            const startTimer = (time: number) => {
                //show warning two minutes before, take extra one minute for safe side
                warningTimeout = setTimeout(showWarning, time - 1000 * 3 * 60);
                logoutTimeout = setTimeout(() => doLogout(onload!.menus.find((menu: any) => menu.name === "Log Out").url), time - 1000);
            }

            const clearSessionTimeoutChatBlock = () => {
                window.dispatchEvent(new CustomEvent("minimizeDeveloperChat", { detail: { sessionTimeoutModalVisible: false } }));
            };

            const showWarning = () => {
                window.dispatchEvent(new CustomEvent("minimizeDeveloperChat", { detail: { sessionTimeoutModalVisible: true } }));
                const logoutMenuUrl = onload!.menus.find((menu: any) => menu.name === "Log Out").url;

                Modal.confirm({
                    keyboard: false,
                    icon: null,
                    okText: "Continue session",
                    mask: true,
                    // Clicks on the dimmed backdrop must NOT trigger onCancel — that was logging users out.
                    maskClosable: false,
                    cancelText: "Logout",
                    zIndex: 10050,
                    onCancel: () => {
                        clearSessionTimeoutChatBlock();
                        doLogout(logoutMenuUrl);
                    },
                    onOk: () => {
                        clearSessionTimeoutChatBlock();
                        window.dispatchEvent(new Event('resetTimer'));
                        DataUtils.keepAlive(authentication).get().catch((error) => console.error(error));
                    },
                    cancelButtonProps: { type: "danger", style: { marginRight: "8px" } },
                    title:
                        <>
                            <Title level={4} className={`color-gray-5`}>
                                You will automatically be logged out in
                            </Title>
                            <Title level={4} >
                                <Statistic.Countdown
                                    className="lgout-countdown"
                                    onFinish={() => {
                                        clearSessionTimeoutChatBlock();
                                        doLogout(logoutMenuUrl);
                                    }}
                                    value={Date.now() + 120000}
                                    format="m[ minute(s) ]ss[ seconds ]"
                                />
                            </Title>
                        </>
                });
            }
            const sessionTimeOutInMilliseconds = onload.sessionTimeOut.value * 1000 * 60;
            startTimer(sessionTimeOutInMilliseconds);
            window.addEventListener('resetTimer', () => {
                if (warningTimeout) clearTimeout(warningTimeout);
                if (logoutTimeout) clearTimeout(logoutTimeout);

                startTimer(sessionTimeOutInMilliseconds);
            });
        }
    }, [onload]);

    useEffect(() => {
        const authentication: Authentication = getAuthenticationDetails();
        DataUtils.preference(authentication).get()
            .then((response: AxiosResponse) => {
                let prefData: any = {};
                try {
                    prefData = { ...JSON.parse(response.data.preference || '{}') }
                } catch (e) { console.error(e) }
                if (!(prefData?.preferences?.isSplashCompleted === true)) {
                    setIsSplashOpen(true);
                }

                const selectedBrandPreference = prefData?.preferences?.global?.selectedBrand || {};
                if (selectedBrandPreference) {
                    setSelectedBrand(selectedBrandPreference);
                }

                setPref(prefData);

            }).catch((error: AxiosError<any,any>) => {
                console.error(error);
                setPref(preferenceDefaults);
            });
    }, []);

    useEffect(() => {
        const authentication: Authentication = getAuthenticationDetails();

        if (onload && !picture)
            fetchPicture(authentication);
    }, [onload, picture]);

    useEffect(() => {
        const authentication: Authentication = getAuthenticationDetails();

        if (!logo) {
            fetchLogo(authentication);
        }
    }, [logo]);

    const fetchPicture = (authentication: any, image?: any) => {
        if (image)
            setPicture(image?.dataUrl);
        else {
            DataUtils.picture(authentication).get(onload.user.id).then(picture => {
                const reader = new window.FileReader();
                reader.readAsDataURL(picture.data);
                reader.onload = () => {
                    setPicture(reader.result);
                }
            });
        }
    }

    const fetchLogo = (authentication: any, image?: any) => {
        if (image)
            setLogo(image?.logo);
        else {
            DataUtils.logo(authentication).get().then((logo) => {
                if (logo?.status === 204) {
                    setLogo(undefined);
                } else {
                    const reader = new window.FileReader();
                    reader.readAsDataURL(logo.data);
                    reader.onload = () => {
                        setLogo(reader.result);
                    }
                }
            });
        }
    }

    const fetchBanner = (authentication: any, image?: any) => {
        if (image)
            setBanner(image?.logo)
        else {
            DataUtils.banner(authentication).get().then((banner) => {
                if (banner?.status === 204) {
                    setBanner(undefined);
                } else {
                    const reader = new window.FileReader();
                    reader.readAsDataURL(banner.data);
                    reader.onload = () => {
                        setBanner(reader.result);
                    }
                }
            });
        }
    }

    const onMenuImageUpdate = (image: any, imageType: "logo" | "banner" | "picture") => {
        const authentication: Authentication = getAuthenticationDetails();

        if (imageType === 'logo') {
            fetchLogo(authentication, image);
        } else if (imageType === "banner") {
            fetchBanner(authentication, image);
        } else if (imageType === 'picture') {
            fetchPicture(authentication, image);
        }
    }

    useEffect(() => {
        const authentication: Authentication = getAuthenticationDetails();

        if (!banner) {
            fetchBanner(authentication);
        }
    }, [banner])

    const isSRPageRouteExisting = window.location.hash.includes(UrlConfigs.ROUTES.SUPPORT.subRoutes.SERVICE_REQUESTS.url);
    const collapsedWidth = 60, expandedWidth = 240, leftMargin = 12;

    let caseManagementUrl = onload?.caseManagementUrl || "";
    const CASE_MANAGEMENT_UI_URL = caseManagementUrl.endsWith('/') ? caseManagementUrl.substr(0, caseManagementUrl.length - 1) : caseManagementUrl;

    return (
        <>{
            pref && onload && userPermission && selectedBrand && userFeatures ?
                <Layout style={{ minHeight: '100vh' }}>
                    <ApplicationContext.Provider
                        value={{
                            onload: { ...onload, ...(!!financialInstitutions && { financialInstitution: financialInstitutions }) },
                            permissions: userPermission,
                            selectedBrand: selectedBrand,
                            userFeatures: userFeatures,
                            preferences: pref,
                            setSelectedBrand: (selectedBrand) => setSelectedBrand(selectedBrand),
                            setFinancialInstitutions: (fi) => setFinancialInstitutions(fi),
                            setPreferences: (preferences) => setPref(preferences),
                            setUserFeatures: (userFeatures) => setUserFeatures(userFeatures),
                        }}
                    >
                        <FloatingChatButton userFeatures={userFeatures} />
                        <WidgetContext.Provider value={{ YccNewCaseManagementWidget, setYccNewCaseManagementWidget }} >
                            <Affix>
                                <Layout.Sider
                                    className="custom-fixed-sider"
                                    trigger={
                                        <div style={{ left: "4px" }} className={`label-1 color-gray-5 align-left ${collapsed ? "padding-left-lg padding-xl-horizontal font-size-sm" : "padding-left-xl padding-lg-horizontal"}`}>
                                            {
                                                collapsed ?
                                                    <Icon style={{ width: "0px" }} component={() => <FontAwesomeIcon icon={faChevronRight} />} className="margin-right-xs font-size-xl" /> :
                                                    <React.Fragment>
                                                        <Icon style={{ width: "0px", marginRight: "18px" }} component={() => <FontAwesomeIcon icon={faChevronLeft} />} className="font-size-xl" />
                                                        <span className="margin-left-md font-size-sm">
                                                            {LabelConfigs.CLOSE.toUpperCase()}
                                                        </span>
                                                    </React.Fragment>
                                            }
                                        </div>
                                    }
                                    collapsible
                                    width={expandedWidth}
                                    theme='light'
                                    collapsedWidth={isMD ? `${collapsedWidth}px` : 0}
                                    collapsed={collapsed}
                                    onClick={() => toggleCollapsed('LAYOUT')}
                                    onCollapse={() => toggleCollapsed('ARROW')}
                                    style={isMD ? {} : { position: "fixed", height: "100vh", overflowY: "auto", overflowX: "hidden", top: 0, zIndex: 9999 }}
                                >
                                    <div style={isMD ? { position: "fixed", width: collapsed ? `${collapsedWidth}px` : `${expandedWidth}px` } : {}}>
                                        <img
                                            alt={LabelConfigs.ENVESTNET_YODLEE}
                                            className='margin-top-md'
                                            src={collapsed ? logo || Logos.EnvestnetYodlee : banner || Logos.EnvestnetYodleeLabel}
                                            onClick={() => toggleCollapsed('LOGO')}
                                            style={{
                                                cursor: "pointer",
                                                display: "block",
                                                marginRight: "auto",
                                                marginLeft: collapsed ? "auto" : leftMargin + 'px',
                                                maxHeight: "48px",
                                                maxWidth: collapsed ? `${collapsedWidth - leftMargin}px` : `${expandedWidth - (leftMargin + 2)}px`
                                            }}
                                        />
                                        <MenuItems
                                            history={history}
                                            helpContentMap={onload?.helpConfig || {}}
                                            isCollapsed={collapsed}
                                            picture={picture}
                                            setIsSplashOpen={setIsSplashOpen}
                                            toggleCollapsed={toggleCollapsed}
                                            userFeatures={userFeatures}
                                            brandproducts={brandProducts}
                                        />
                                    </div>
                                </Layout.Sider>
                            </Affix>
                            <Layout onClick={() => !collapsed && setCollapsed(true)} style={{ background: "white" }}>

                                <MenuContext.Provider value={{
                                    menuCollapsed: collapsed,
                                    setMenuCollapsed: setCollapsed
                                }}>
                                    <div
                                        style={{
                                            display: 'flex',
                                            // flex: 1,
                                            minWidth: "100%",
                                            maxWidth: "100%",
                                            minHeight: "100%",
                                            maxHeight: "100%",
                                            flexDirection: 'column'
                                        }}
                                        onDragOver={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.nativeEvent.stopImmediatePropagation();
                                            e.nativeEvent.stopPropagation();
                                        }}
                                        onDrop={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            e.nativeEvent.stopImmediatePropagation();
                                            e.nativeEvent.stopPropagation();
                                        }}
                                    >
                                        {
                                            <div style={{ display: isSRPageRouteExisting ? "block" : "none" }}>
                                                {/* CaseManagement is feature = 1 */}
                                                {
                                                //    userFeatures.find(feat => feat.id === 1) ?
                                                        <PackageLoader
                                                            bundleUrl={`${(CASE_MANAGEMENT_UI_URL)}/widgets/ycc-new-case-management-widget/v1.0.0/bundle.js`}
                                                            packageName={"@yodlee/ycc-new-case-management-widget"}
                                                            contextValue={YccNewCaseManagementWidget}
                                                            contextSetter={setYccNewCaseManagementWidget}>
                                                            <ServiceRequestPage hideSRPage={!isSRPageRouteExisting} history={history} {...rest} />
                                                        </PackageLoader> 
                                                //        <ServiceRequestPage hideSRPage={!isSRPageRouteExisting} history={history} {...rest} />
                                                }
                                            </div>
                                        }
                                        <Switch>
                                            <Route exact path={UrlConfigs.ROUTES.ROOT.url} render={(props) => <DashboardPage onload={onload} {...props} />} />
                                            <Route path={UrlConfigs.ROUTES.DASHBOARD.url} render={(props) => <DashboardPage onload={onload} {...props} />} />
                                            <Route path={UrlConfigs.ROUTES.SUPPORT.url} component={SupportPage} />
                                            <Route path={UrlConfigs.ROUTES.ANALYTICS.url} component={AnalyticsPage} />
                                            <Route path={UrlConfigs.ROUTES.STATUS.url} component={StatusPage} />
                                            <Route path={UrlConfigs.ROUTES.PRODUCTS.url} render={(props) => <ProductPage {...props} />} />
                                            <Route path={UrlConfigs.ROUTES.TRIALS.subRoutes.CREDIT_ACCELARATOR.url} render={(props) => <TrialsIframePage key= {uuidv4()} {...props} view='creditMatrix' />} />                                          
                                            <Route path={UrlConfigs.ROUTES.TRIALS.subRoutes.VERIFICATION.url} render={(props) => <TrialsIframePage  key= {uuidv4()} {...props} view='verification' />} />
                                            <Route path={UrlConfigs.ROUTES.TRIALS.subRoutes.PFM.url} render={(props) => <TrialsIframePage {...props} view='pfm' />} />
                                            <Route path={UrlConfigs.ROUTES.TRIALS.subRoutes.CONSUMER_INSIGHTS.url} render={(props) => <ConsumerInsightsPage {...props} view='consumerInsights' />} />
                                            <Route path={UrlConfigs.ROUTES.TRIALS.subRoutes.SMB.url} render={(props) => <TrialsIframePage key= {uuidv4()} {...props} view='smb' />} />
                                            <Route path={UrlConfigs.ROUTES.TRIALS.subRoutes.ATV.url} render={(props) => <TrialsIframePage key= {uuidv4()} {...props} view='atv' />} />
                                            <Route path={UrlConfigs.ROUTES.TRIALS.subRoutes.ADVISOR_INSIGHTS.url} render={(props) => <TrialsIframePage key= {uuidv4()} {...props} view='advisor-insights' />} />
                                            <Route path={UrlConfigs.ROUTES.IEB.subRoutes.SEGMENTATION.url} render={(props) => <ProductAndMarketingInsightsPage {...props} view='segmentation'/>} />
                                            <Route path={UrlConfigs.ROUTES.IEB.subRoutes.PRODUCT_INSIGHTS.url} render={(props) => <ProductAndMarketingInsightsPage {...props} view='insights'/>} />
                                            <Route path={UrlConfigs.ROUTES.IEB.subRoutes.INSIGHTS_DASHBOARD.url} render={(props) => <ProductAndMarketingInsightsPage {...props} view='dashboard'/>} />
                                            <Route path={UrlConfigs.ROUTES.MANAGEMENT_REPORTING.subRoutes.FASTLINK_JOURNEY_ANALYTICS.url} render={(props) => <ManagementReportingPage {...props} view='fastlink-journey-analytics'/>} />
                                            <Route path={UrlConfigs.ROUTES.MANAGEMENT_REPORTING.subRoutes.AGGREGATION.url} render={(props) => <ManagementReportingPage {...props} view='aggregation'/>} />
                                            <Route path={UrlConfigs.ROUTES.MANAGEMENT_REPORTING.subRoutes.VERIFICATION.url} render={(props) => <ManagementReportingPage {...props} view='verification'/>} />
                                            <Route path={UrlConfigs.ROUTES.DEVELOPER_CHAT.url} component={DeveloperChatPage} />
                                            <Route path={UrlConfigs.ROUTES.FEATURE.url} component={FeatureSettingPage} />
                                            <Route
                                                path={UrlConfigs.ROUTES.SETTINGS.url}
                                                render={
                                                    (props) =>
                                                        <SettingsPage
                                                            {...props}
                                                            onLogoUpdate={(logo: any) => onMenuImageUpdate(logo, "logo")}
                                                            onBannerUpdate={(banner: any) => onMenuImageUpdate(banner, "banner")}
                                                        />
                                                }
                                            />
                                            <Route path={UrlConfigs.ROUTES.INTERNAL_TOOLS.url} component={InternalToolsPage} />
                                            <Route path={UrlConfigs.ROUTES.MY_ACCOUNT.url} render={() => <MyAccountPage onPictureUpdate={(picture: any) => onMenuImageUpdate(picture, "picture")} />} />
                                        </Switch>
                                    </div>
                                </MenuContext.Provider>

                            </Layout>
                        </WidgetContext.Provider>
                    </ApplicationContext.Provider>
                </Layout> :
                <Row type="flex" justify="center" align="middle" style={{ height: "100vh" }}>
                    <Col>
                        <Spin size="large" indicator={<Icon type="loading" />} />
                    </Col>
                </Row>
        }
            {
                isSplashOpen && <SplashPage isSplashActive={isSplashOpen} onChangeSplashActive={setIsSplashOpen} />
            }
        </>
    )
}

export default HomePage;